"use client";

import { Compass, FileCheck as FileSignature, FileText, Send, Search, Award } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
const steps = [{
  numeral: "I.",
  eyebrow: "ANALYSIS",
  title: "Current Tech Audit",
  description: "We begin by auditing your existing hospitality tech stack. We identify bottlenecks in your PMS, booking engine, and channel manager integrations that are currently hindering your revenue potential.",
  Icon: Compass
}, {
  numeral: "II.",
  eyebrow: "STRATEGY",
  title: "Integration Roadmap",
  description: "Our consultants map out a seamless integration strategy. We align your channel manager with your revenue goals, ensuring real-time rate parity and inventory accuracy across all OTA platforms.",
  Icon: Search
}, {
  numeral: "III.",
  eyebrow: "CONFIGURATION",
  title: "System Optimization",
  description: "We configure your channel manager settings with precision. From automated yield rules to dynamic rate updates, we ensure your system works as a high-performance engine for your property.",
  Icon: FileText
}, {
  numeral: "IV.",
  eyebrow: "DEPLOYMENT",
  title: "Live Implementation",
  description: "Your new channel management setup goes live. We monitor the transition closely, ensuring that every connection to your OTAs is stable, accurate, and fully optimized for maximum visibility.",
  Icon: Send
}, {
  numeral: "V.",
  eyebrow: "VALIDATION",
  title: "Performance Testing",
  description: "We conduct rigorous testing to validate rate parity and booking flow. This is where our technical expertise ensures your revenue management is bulletproof against overbookings and rate errors.",
  Icon: FileSignature
}, {
  numeral: "VI.",
  eyebrow: "GROWTH",
  title: "Continuous Scaling",
  description: "Your system is now a revenue-generating asset. We remain as your partners, providing ongoing support, tech stack updates, and strategic adjustments to keep your property ahead of the competition.",
  Icon: Award
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25,
    rootMargin: "0px 0px -80px 0px"
  });
  const isLeft = index % 2 === 0;
  const {
    Icon
  } = step;
  return <div ref={ref} data-index={index} className="relative grid grid-cols-12 gap-x-6 lg:gap-x-8 py-20 md:py-28 lg:py-32">
      {}
      <div className="hidden lg:flex absolute top-32 left-[calc(33.333%-0.5rem)] w-4 h-4 items-center justify-center z-10" aria-hidden="true">
        <div className="w-4 h-4 rounded-lg flex items-center justify-center transition-all duration-700" style={{
        backgroundColor: "var(--primary)",
        transform: isVisible ? "scale(1)" : "scale(0.6)",
        opacity: isVisible ? 1 : 0.3
      }}>
          <div className="w-1.5 h-1.5 rounded-lg" style={{
          backgroundColor: "var(--background)"
        }} />
        </div>
      </div>

      {}
      <div className={`col-span-12 ${isLeft ? "lg:col-span-5 lg:col-start-1 lg:pr-12" : "lg:col-span-7 lg:col-start-6 lg:pl-12"}`}>
        {}
        <div className="font-default italic leading-none mb-6 transition-all duration-700 ease-out" style={{
        fontSize: "5rem",
        color: "var(--primary)",
        transform: isVisible ? "translateY(0)" : "translateY(12px)",
        opacity: isVisible ? 1 : 0,
        transitionDelay: "0ms"
      }}>
          {step.numeral}
        </div>

        {}
        <div className="font-sans uppercase text-xs tracking-[0.25em] text-muted-foreground mb-4 transition-all duration-700 ease-out" style={{
        transform: isVisible ? "translateY(0)" : "translateY(12px)",
        opacity: isVisible ? 1 : 0,
        transitionDelay: "120ms"
      }}>
          {step.eyebrow}
        </div>

        {}
        <h3 className="font-default text-foreground leading-[1.1] mb-8 transition-all duration-700 ease-out" style={{
        fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
        fontWeight: 400,
        transform: isVisible ? "translateY(0)" : "translateY(12px)",
        opacity: isVisible ? 1 : 0,
        transitionDelay: "240ms"
      }}>
          {step.title}
        </h3>

        {}
        <div className="mb-6 transition-all duration-700 ease-out" style={{
        color: "var(--secondary)",
        transform: isVisible ? "translateY(0)" : "translateY(12px)",
        opacity: isVisible ? 1 : 0,
        transitionDelay: "320ms"
      }} aria-hidden="true">
          <Icon size={40} strokeWidth={1.25} />
        </div>

        {}
        <p className="font-default text-foreground/85 leading-relaxed max-w-md transition-all duration-700 ease-out" style={{
        fontSize: "1.0625rem",
        transform: isVisible ? "translateY(0)" : "translateY(12px)",
        opacity: isVisible ? 1 : 0,
        transitionDelay: "440ms"
      }}>
          {step.description}
        </p>
      </div>
    </div>;
}
export default function Implementation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3576" id="channelmanagerimplementation" className="relative bg-background text-foreground overflow-hidden" style={{
    backgroundImage: "radial-gradient(ellipse at top, hsl(222 47% 7% / 0.025), transparent 60%)"
  }}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
        {}
        <div ref={headerRef} className="grid grid-cols-12 gap-x-6 lg:gap-x-8 mb-24 md:mb-32">
          {}
          <div className="col-span-12 lg:col-span-9">
            <div className="font-sans uppercase text-xs tracking-[0.3em] text-muted-foreground mb-10 md:mb-14 transition-all duration-700 ease-out" style={{
            transform: headerVisible ? "translateY(0)" : "translateY(12px)",
            opacity: headerVisible ? 1 : 0
          }}>
              Our Process — Tech Integration
            </div>
            <h2 className="font-default text-foreground leading-[0.95] transition-all duration-1000 ease-out" style={{
            fontSize: "clamp(3rem, 8vw, 7rem)",
            fontWeight: 400,
            letterSpacing: "-0.02em",
            transform: headerVisible ? "translateY(0)" : "translateY(16px)",
            opacity: headerVisible ? 1 : 0,
            transitionDelay: "120ms"
          }}>
              Channel Manager
              <br />
              <em className="italic" style={{
              color: "var(--primary)",
              fontWeight: 400
            }}>
                Implementation.
              </em>
            </h2>
          </div>

          {}
          <div className="col-span-12 lg:col-span-3 mt-10 lg:mt-auto lg:pb-4">
            <div className="max-w-sm transition-all duration-1000 ease-out" style={{
            transform: headerVisible ? "translateY(0)" : "translateY(12px)",
            opacity: headerVisible ? 1 : 0,
            transitionDelay: "320ms"
          }}>
              <Separator className="mb-6 w-12" style={{
              backgroundColor: "var(--primary)",
              height: "2px"
            }} />
              <p className="font-default text-foreground/80 text-base leading-relaxed">
                Implementing a channel manager is a critical step in modernizing your hotel's revenue strategy. At HotelRevBaBa, we guide you through six deliberate stages, ensuring your tech stack is perfectly aligned with your revenue goals.
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute top-0 bottom-0 left-1/3 w-px" style={{
          backgroundColor: "var(--primary)",
          opacity: 0.4
        }} aria-hidden="true" />

          {}
          {steps.map((step, i) => <TimelineStep key={i} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}